import { useCallback, useEffect, useRef, useState } from 'react';
import { BookmarkPlus, CheckCircle2, Loader2, Search, X } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { useAuth } from '../lib/contextoAuth';
import SelectorCategoria from './SelectorCategoria';
import SelectorUbicacion from './SelectorUbicacion';
import {
  claseDeColor, estadoChip, FILTRO_VACIO, hayFiltro, recordado, recordar, sostener, tocar,
} from './etiquetas';

/* =====================================================================
   VISTAS DEL INVENTARIO · CATÁLOGO VIVO F4 (y F6.1–6.2)
   =====================================================================
   La lista de productos que se puede acotar sin tocar datos:
     · chips de etiqueta: tocar filtra, dos intersectan, sostener excluye;
     · categoría (con todo lo que tiene dentro), lugar raíz y texto, que
       entiende atributos («10k», «Garro», «M3x10»);
     · «Solo lo que hay que reponer» (F6.3): por debajo de su punto de
       reorden o de su mínimo, que ya son columnas del producto;
     · y guardar todo eso como VISTA, que sale arriba como pestaña.
   «Ocultar temporalmente todos los libros» es una vista, no un borrado.
   El filtro se recuerda en este teléfono para esta persona.
   ===================================================================== */
const mensaje = (e) => (e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
const ESPERA_MS = 350;
const SOSTENER_MS = 550;

function Chip({ etiqueta, estado, onTocar, onSostener }) {
  const temporizador = useRef(null);
  const sostenido = useRef(false);
  const empezar = () => {
    sostenido.current = false;
    temporizador.current = setTimeout(() => { sostenido.current = true; onSostener(); }, SOSTENER_MS);
  };
  const terminar = () => clearTimeout(temporizador.current);
  const texto = estado === 'fuera' ? `sin ${etiqueta.nombre}` : etiqueta.nombre;
  return (
    <button type="button" data-chip={etiqueta.id} data-estado={estado}
      aria-pressed={estado === 'dentro'}
      aria-label={estado === 'fuera' ? `Sin la etiqueta ${etiqueta.nombre}` : `Etiqueta ${etiqueta.nombre}`}
      onPointerDown={empezar} onPointerUp={terminar} onPointerLeave={terminar}
      onContextMenu={(e) => { e.preventDefault(); terminar(); if (!sostenido.current) onSostener(); sostenido.current = true; }}
      onClick={() => { if (sostenido.current) { sostenido.current = false; return; } onTocar(); }}
      className={`min-h-11 inline-flex items-center gap-1 rounded-full border px-3 text-sm ${claseDeColor(etiqueta.color)} ${estado === 'dentro' ? 'ring-2 ring-[var(--color-marca)] font-semibold' : ''} ${estado === 'fuera' ? 'line-through opacity-80' : ''}`}>
      {texto}
    </button>
  );
}

export default function VistasInventario() {
  const { usuario } = useAuth() || {};
  const idUsuario = usuario?.id;
  const [filtro, setFiltro] = useState(() => recordado(idUsuario));
  const [texto, setTexto] = useState(filtro.texto || '');
  const [etiquetas, setEtiquetas] = useState([]);
  const [vistas, setVistas] = useState([]);
  const [vistaActiva, setVistaActiva] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [lugar, setLugar] = useState(null);
  const [guardar, setGuardar] = useState({ abierto: false, nombre: '', compartida: false });

  const cargarVistas = useCallback(() => api.get('/catalogo-vivo/vistas')
    .then((r) => setVistas(r.vistas || [])).catch(() => setVistas([])), []);
  useEffect(() => {
    api.get('/catalogo-vivo/etiquetas').then((r) => setEtiquetas(r.etiquetas || [])).catch(() => setEtiquetas([]));
    cargarVistas();
  }, [cargarVistas]);

  // El texto se manda al dejar de escribir; lo demás, al momento.
  useEffect(() => {
    const t = setTimeout(() => setFiltro((f) => (f.texto === texto ? f : { ...f, texto })), ESPERA_MS);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    let vigente = true;
    recordar(idUsuario, filtro);
    const t = setTimeout(() => {
      setCargando(true);
      api.post('/catalogo-vivo/filtrar', filtro)
        .then((r) => { if (vigente) { setError(null); setResultado(r); } })
        .catch((e) => { if (vigente) setError(mensaje(e)); })
        .finally(() => { if (vigente) setCargando(false); });
    }, 0);
    return () => { vigente = false; clearTimeout(t); };
  }, [filtro, idUsuario]);

  const usarVista = (v) => {
    setVistaActiva(v?.id ?? null);
    const f = v ? { ...FILTRO_VACIO, ...v.filtro } : { ...FILTRO_VACIO };
    setTexto(f.texto || '');
    setLugar(null);
    setFiltro(f);
  };

  async function guardarVista(e) {
    e.preventDefault();
    setError(null);
    try {
      const r = await api.post('/catalogo-vivo/vistas', { nombre: guardar.nombre.trim(), filtro, compartida: guardar.compartida });
      setGuardar({ abierto: false, nombre: '', compartida: false });
      setHecho(`Listo: la vista «${r.vista.nombre}» quedó arriba${r.vista.compartida ? ' y la ve toda tu empresa' : ''}.`);
      setVistaActiva(r.vista.id);
      cargarVistas();
    } catch (err) {
      setError(mensaje(err));
    }
  }

  const motivoGuardar = !hayFiltro(filtro) ? 'Elige al menos un filtro para guardarlo como vista.'
    : !guardar.nombre.trim() ? 'Escribe el nombre de la vista.' : null;

  return (
    <div className="space-y-4" data-catalogo-vivo="vistas">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Vistas del inventario</h2>
        <p className="text-sm text-gray-500 mt-0.5">Tus productos acotados por etiqueta, categoría, lugar o dato, sin tocar nada.</p>
      </div>
      <Ayuda id="catalogo_vivo.vistas.pantalla" />

      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" data-catalogo-vivo="pestanas-vistas">
        <button type="button" role="tab" aria-selected={vistaActiva === null} onClick={() => usarVista(null)}
          className={`min-h-11 shrink-0 rounded-xl border px-3 text-sm font-semibold ${vistaActiva === null ? 'border-[var(--color-marca)] text-[var(--color-marca)]' : 'border-gray-200 text-gray-700'}`}>
          Todo
        </button>
        {vistas.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={vistaActiva === v.id} onClick={() => usarVista(v)}
            data-vista={v.id}
            className={`min-h-11 shrink-0 rounded-xl border px-3 text-sm font-semibold ${vistaActiva === v.id ? 'border-[var(--color-marca)] text-[var(--color-marca)]' : 'border-gray-200 text-gray-700'}`}>
            {v.nombre}{v.mia ? '' : ` · de ${v.de}`}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="h-4 w-4 text-gray-500 absolute left-3 top-4" aria-hidden="true" />
        <label htmlFor="cv-buscar-inventario" className="sr-only">Buscar en el inventario</label>
        <input id="cv-buscar-inventario" type="search" value={texto} onChange={(e) => setTexto(e.target.value)}
          placeholder="Busca: 10k, Garro, M3x10…" autoComplete="off"
          className="w-full min-h-12 rounded-xl border border-gray-300 pl-9 pr-3 text-base" />
      </div>
      <Ayuda id="catalogo_vivo.vistas.buscar" />

      {etiquetas.length > 0 && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-2" data-catalogo-vivo="chips">
            {etiquetas.map((e) => (
              <Chip key={e.id} etiqueta={e} estado={estadoChip(filtro, e.id)}
                onTocar={() => { setVistaActiva(null); setFiltro((f) => tocar(f, e.id)); }}
                onSostener={() => { setVistaActiva(null); setFiltro((f) => sostener(f, e.id)); }} />
            ))}
          </div>
          <Ayuda id="catalogo_vivo.vistas.chips" />
        </div>
      )}

      <label className="inline-flex items-center gap-2 min-h-11 text-sm font-semibold text-gray-800" data-catalogo-vivo="bajo-minimo">
        <input type="checkbox" className="h-5 w-5" checked={Boolean(filtro.bajo_minimo)}
          onChange={(e) => { setVistaActiva(null); setFiltro((f) => ({ ...f, bajo_minimo: e.target.checked })); }} />
        Solo lo que hay que reponer
      </label>
      <Ayuda id="catalogo_vivo.vistas.reponer" />

      <details className="rounded-xl border border-gray-200 p-3">
        <summary className="min-h-11 flex items-center text-sm font-semibold text-gray-800 cursor-pointer">Más filtros: categoría y lugar</summary>
        <div className="space-y-3 mt-2">
          <SelectorCategoria id="cv-vista-categoria" etiqueta="Categoría (con todo lo que tiene dentro)"
            valor={filtro.categorias?.[0] ?? null}
            onElegir={(id) => { setVistaActiva(null); setFiltro((f) => ({ ...f, categorias: id ? [id] : [] })); }} />
          <SelectorUbicacion id="cv-vista-lugar" etiqueta="Lugar (y lo que tiene dentro)" valor={lugar}
            onElegir={(u) => { setLugar(u); setVistaActiva(null); setFiltro((f) => ({ ...f, ubicacion_raiz: u?.id ?? null })); }} />
        </div>
      </details>

      {hayFiltro(filtro) && (
        <button type="button" onClick={() => usarVista(null)}
          className="min-h-11 inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 text-sm font-semibold text-gray-800">
          <X className="h-4 w-4" aria-hidden="true" /> Quitar todos los filtros
        </button>
      )}

      {hecho && <p role="status" className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      {cargando && !resultado && (
        <p className="text-sm text-gray-600 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Buscando…</p>
      )}
      {resultado && (
        <section className="space-y-2" aria-busy={cargando}>
          <p className="text-sm text-gray-700" data-catalogo-vivo="cuantos">
            {resultado.total === 0 ? 'Ningún producto con estos filtros.' : `${resultado.total} producto(s)${resultado.total > resultado.mostrados ? `; se muestran los primeros ${resultado.mostrados}` : ''}.`}
          </p>
          {resultado.total === 0 && hayFiltro(filtro) && (
            <p className="text-sm text-gray-600">Quita un filtro o una etiqueta para ver más. Tus productos siguen ahí.</p>
          )}
          <ul className="space-y-1">
            {resultado.productos.map((p) => (
              <li key={p.id} className="rounded-lg border border-gray-100 p-2" data-producto={p.id}>
                <span className="block text-sm font-semibold text-gray-900 break-words">{p.nombre}</span>
                <span className="block text-xs text-gray-600 break-words">
                  <span className="font-mono">{p.sku}</span>{p.categoria ? ` · ${p.categoria}` : ''} · {Number(p.existencias)} {String(p.unidad_medida || '').toLowerCase()}
                </span>
                {p.bajo_minimo && (
                  <span className="block text-xs text-amber-800" data-reponer={p.id}>
                    Por reponer: quedan {Number(p.existencias)} y el punto de reorden es {Number(p.punto_reorden) || Number(p.stock_minimo)}.
                  </span>
                )}
                {p.etiquetas?.length > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {p.etiquetas.map((e) => (
                      <span key={e.id} className={`rounded-full border px-2 text-xs ${claseDeColor(e.color)}`}>{e.nombre}</span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-gray-200 p-3 space-y-2" data-catalogo-vivo="guardar-vista">
        {!guardar.abierto ? (
          <button type="button" onClick={() => setGuardar((g) => ({ ...g, abierto: true }))}
            className="w-full min-h-12 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 text-sm font-semibold text-gray-800">
            <BookmarkPlus className="h-5 w-5" aria-hidden="true" /> Guardar estos filtros como vista
          </button>
        ) : (
          <form onSubmit={guardarVista} className="space-y-2">
            <label htmlFor="cv-nombre-vista" className="block text-sm font-semibold text-gray-800">Nombre de la vista</label>
            <input id="cv-nombre-vista" value={guardar.nombre} maxLength={60} autoComplete="off"
              onChange={(e) => setGuardar((g) => ({ ...g, nombre: e.target.value }))}
              placeholder="Por ejemplo: Mis componentes" className="w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
            <label className="inline-flex items-center gap-2 min-h-11 text-sm text-gray-800">
              <input type="checkbox" className="h-5 w-5" checked={guardar.compartida}
                onChange={(e) => setGuardar((g) => ({ ...g, compartida: e.target.checked }))} />
              Que la vea toda mi empresa
            </label>
            <Ayuda id="catalogo_vivo.vistas.guardar" />
            <button type="submit" disabled={Boolean(motivoGuardar)}
              className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4 disabled:opacity-60">
              <BookmarkPlus className="h-5 w-5" aria-hidden="true" /> Guardar vista
            </button>
            {motivoGuardar && <p className="text-xs text-amber-800" data-motivo="guardar-vista">{motivoGuardar}</p>}
          </form>
        )}
      </section>
    </div>
  );
}
